import { useState } from 'react';

export default function CTABanner() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5uvn2us8bb84h5iejrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-block px-4 py-2 bg-[#FF6B35]/10 rounded-full mb-6">
              <span className="text-[#FF6B35] font-bold text-sm tracking-wide">CONTACT US</span>
            </div>
            
            <h3 className="text-5xl lg:text-6xl font-black text-[#0F4C81] mb-6 leading-tight">
              Ready to Start Your Project?
            </h3>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Contact LineFlow Underground & Civil today for professional consultation. We're here to help with all your plumbing and civil engineering needs.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FF6B35] to-[#FF8C5A] rounded-xl flex-shrink-0">
                  <i className="ri-phone-fill text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 font-medium">Phone</div>
                  <div className="flex flex-col gap-1">
                    <a href="tel:0410880821" className="text-xl font-bold text-[#0F4C81] hover:text-[#FF6B35] transition-colors cursor-pointer">
                      0410 880 821
                    </a>
                    <a href="tel:0412212971" className="text-xl font-bold text-[#0F4C81] hover:text-[#FF6B35] transition-colors cursor-pointer">
                      0412 212 971
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#0F4C81] to-[#1E88E5] rounded-xl flex-shrink-0">
                  <i className="ri-mail-fill text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 font-medium">Email</div>
                  <a href="mailto:info@lineflow.com.au" className="text-xl font-bold text-[#0F4C81] hover:text-[#FF6B35] transition-colors cursor-pointer">
                    info@lineflow.com.au
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FF6B35] to-[#FF8C5A] rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-fill text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 font-medium">Service Area</div>
                  <p className="text-lg font-bold text-[#0F4C81]">Metro Area & Surrounding Regions</p>
                </div>
              </div>
            </div>

            {/* Business Info */}
            <div className="p-6 bg-gradient-to-br from-[#0F4C81] to-[#1E88E5] rounded-2xl">
              <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <i className="ri-information-fill text-xl"></i>
                Why Choose LineFlow?
              </h4>
              <div className="space-y-2 text-white/90 text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#FF6B35]"></i>
                  <span>Licensed professional team, fully insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#FF6B35]"></i>
                  <span>Specialized in new home construction plumbing</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#FF6B35]"></i>
                  <span>Underground & civil engineering experts</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#FF6B35]"></i>
                  <span>Quality craftsmanship, guaranteed results</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10">
            <h4 className="text-3xl font-bold text-[#0F4C81] mb-2">Get a Project Quote</h4>
            <p className="text-gray-600 mb-6">Fill out the form and we'll contact you soon</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm"
                  placeholder="Enter your name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="new-home">New Home Plumbing</option>
                  <option value="underground">Underground Piping</option>
                  <option value="civil">Civil Engineering</option>
                  <option value="commercial">Commercial Project</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors resize-none text-sm"
                  placeholder="Describe your project needs..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-[#FF6B35] to-[#FF8C5A] text-white text-lg font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                  <p className="text-green-800 font-semibold text-center">Success! We'll contact you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                  <p className="text-red-800 font-semibold text-center">Submission failed. Please try again.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
